import { faTriangleExclamation, faCheck, faXmark, faTimes, faPlus } from '@fortawesome/pro-light-svg-icons';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ExamCoverageRow } from '@/app/(main)/_components/search-modal/_components/exam-coverage-row.component';
import { PartnerExamsCoverage, ProgressiveSearchModalKeys } from '@/app/(main)/_components/search-modal/search-modal.types';
import { SheetHeader, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { openSearchModal, useSearchState } from '@/store/search/search.store';

interface ExamsCoverageProps {
  coverage?: PartnerExamsCoverage;
  dataTestId?: string;
  onAddMoreExams?: () => void;
  onSeeSchedules?: () => void;
  onClose?: () => void;
}

export function ExamsCoverage({
  coverage,
  dataTestId = DataTestIdEnum.SEARCH_CARD_EXAMS_MODAL,
  onAddMoreExams,
  onSeeSchedules,
  onClose,
}: ExamsCoverageProps = {}) {
  const { handleJump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const storedCoverage = useStore(useSearchState, (state) => state.examsCoverage);
  const examsCoverage = coverage ?? storedCoverage;

  if (!examsCoverage) {
    return null;
  }

  const { partnerName, isFullCoverage, total, offeredProcedures, notOfferedProcedures } = examsCoverage;

  const handleAddMoreExams = onAddMoreExams
    ?? (() => handleJump({ key: ProgressiveSearchModalKeys.SELECT_MORE_EXAMS }));
  const handleClose = onClose ?? (() => openSearchModal(false));

  return (
    <div className="flex flex-col h-full min-h-0" data-testid={dataTestId}>
      <SheetHeader className="flex-row items-center justify-between gap-2 px-6 pt-5 pb-4 space-y-0">
        <div className="flex items-center gap-2">
          {!isFullCoverage && <Icon icon={faTriangleExclamation} size="lg" className="text-danger-600" />}
          <Typography as="p" variant={TypographyVariant.MODAL_TITLE}>
            {isFullCoverage ? 'Exames selecionados' : 'Atenção'}
          </Typography>
        </div>
        <SheetClose onClick={handleClose} data-testid={DataTestIdEnum.SEARCH_CARD_EXAMS_MODAL_CLOSE}>
          <Icon icon={faTimes} className="text-gray-500 hover:text-gray-900 text-2xl" />
        </SheetClose>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="px-6">
          {isFullCoverage
            ? (
                <Typography as="p" variant={TypographyVariant.BODY_TEXT} className="text-gray-700">
                  <span className="font-bold">{partnerName}</span>
                  <br />
                  {total}
                  {' '}
                  exames adicionados
                </Typography>
              )
            : (
                <Typography as="p" variant={TypographyVariant.BODY_TEXT} className="text-gray-700">
                  <span className="font-bold">{partnerName}</span>
                  {' '}
                  não realiza todos os exames selecionados.
                </Typography>
              )}
        </div>

        {!isFullCoverage && (
          <>
            <div className="flex items-center gap-3 px-6 pt-4 pb-1">
              <Icon icon={faXmark} size="lg" className="text-danger-700" />
              <Typography as="p" variant={TypographyVariant.BODY_TEXT} className="font-bold text-danger-700">
                Exames não oferecidos:
              </Typography>
            </div>
            {notOfferedProcedures.map((procedure) => (
              <div key={procedure.id} className="px-6 py-4">
                <Typography as="p" variant={TypographyVariant.BODY_TEXT} className="font-semibold text-danger-700">
                  {procedure.name}
                </Typography>
              </div>
            ))}

            <div className="flex items-center gap-3 px-6 pt-4 pb-1 border-t border-gray-300">
              <Icon icon={faCheck} size="lg" className="text-green-500" />
              <Typography as="p" variant={TypographyVariant.BODY_TEXT} className="font-bold text-green-500">
                Exames oferecidos:
              </Typography>
            </div>
          </>
        )}

        {offeredProcedures.map((procedure) => (
          <ExamCoverageRow key={procedure.id} id={procedure.id} name={procedure.name} price={procedure.price} />
        ))}
      </div>

      <div className="flex flex-col gap-4 py-5 px-6 shadow-[0_-2px_6px_rgba(0,0,0,0.15)]">
        <Button
          variant="secondary"
          color="primary"
          startIcon={faPlus}
          onClick={handleAddMoreExams}
          data-testid={DataTestIdEnum.SEARCH_CARD_EXAMS_MODAL_ADD_MORE_EXAMS_BUTTON}
        >
          Adicionar mais exames
        </Button>
        <Button
          color="primary"
          data-testid={DataTestIdEnum.SEARCH_CARD_EXAMS_MODAL_SEE_SCHEDULES_BUTTON}
          onClick={onSeeSchedules}
        >
          Ver horários disponíveis
        </Button>
      </div>
    </div>
  );
}
